import React, { useState, useEffect } from 'react';
import BottomNav from '../BottomNav';
import toast from 'react-hot-toast';
import { createQc } from '../../utils/api';

const Qcform = ({ onSuccess, dutyId }) => {
    const [isChecked, setIsChecked] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Initial state with one form entry by default
    const [forms, setForms] = useState([
        { id: Date.now(), missionId: '', status: 'submitted' }
    ]);

    // Handle input changes for a specific form entry
    const handleInputChange = (id, field, value) => {
        setForms(forms.map(form =>
            form.id === id ? { ...form, [field]: value } : form
        ));
    };

    // Add a new form entry
    const addForm = () => {
        setForms([
            ...forms,
            { id: Date.now(), missionId: '', status: 'submitted' }
        ]);
    };

    // Remove a form entry
    const removeForm = (id) => {
        if (forms.length > 1) {
            setForms(forms.filter(form => form.id !== id));
        } else {
            alert("You must have at least one form.");
        }
    };

    // Handle final submission
    const handleSubmit = async (e) => {
        e.preventDefault();

        const isValid = forms.every(form =>
            form.missionId.trim() !== '' &&
            form.status === 'submitted'
        );

        if (!isValid) {
            alert("Please fill in all fields and check the confirmation box.");
            return;
        }

        if (!isChecked) {
            alert("Please check the confirmation box.");
            return;
        }

        const dutyId = dutyId || localStorage.getItem('active_duty_id');
        if (!dutyId) {
            toast.error("Error: No Active Duty ID found. Please contact support or clear cache & restart.");
            return;
        }

        setIsSubmitting(true);
        try {
            const submitQcData = forms.map(async (form) => {
                await createQc(form.missionId, dutyId);
            })
            await Promise.all(submitQcData);
            toast.success("All Qc missions submitted successfully!");
            setIsSubmitting(false);
            localStorage.removeItem('qc_pending');
            if (onSuccess) {
                onSuccess();
            }
        } catch (error) {
            console.error('Error submitting qc missions:', error);
            toast.error('Failed to submit qc missions: ' + error.message);
            setIsSubmitting(false);
        }
        setIsSubmitting(false);
    };

    const handleSkip = async () => {
        const confirmSkip = window.confirm("Are you sure you want to skip QC?");
        if (!confirmSkip) return;

        const dutyId = dutyId || localStorage.getItem('active_duty_id');
        if (!dutyId) {
            toast.error("Error: No Active Duty ID found. Please contact support or clear cache & restart.");
            return;
        }

        setIsSubmitting(true);
        try {
            toast.success("QC skipped successfully!");
            localStorage.removeItem('qc_pending');
            if (onSuccess) onSuccess();
        } catch (error) {
            console.error('Error skipping QC:', error);
            toast.error('Failed to skip QC: ' + error.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="min-h-[100dvh] bg-gray-50 flex flex-col font-sans">
            <div className="flex-1 overflow-y-auto pb-48">
                <div className="max-w-md mx-auto px-5 pt-8 relative">

                    <div className="mb-6">
                        <h1 className="text-xl font-bold text-gray-900">QC Checklist</h1>
                        <p className="text-gray-500 text-xs mt-1">Review and submit mission details.</p>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-4">
                        {forms.map((form, index) => (
                            <div key={form.id} className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 relative">

                                <div className="flex items-center justify-between mb-3">
                                    <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">QC Entry {index + 1}</span>
                                    {forms.length > 1 && (
                                        <button
                                            type="button"
                                            onClick={() => removeForm(form.id)}
                                            className="text-gray-400 hover:text-red-500 transition-colors"
                                        >
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                        </button>
                                    )}
                                </div>

                                <div className="space-y-3">
                                    <div>
                                        <input
                                            type="number"
                                            value={form.missionId}
                                            onChange={(e) => handleInputChange(form.id, 'missionId', e.target.value)}
                                            placeholder="Mission ID"
                                            className="w-full bg-white border-b border-gray-200 px-0 py-2 text-gray-900 placeholder-gray-400 text-base focus:outline-none focus:border-[#00BFA5] transition-colors rounded-none"
                                            required
                                        />
                                    </div>

                                    <div>
                                        <div className="relative">
                                            <select
                                                value={form.status}
                                                onChange={(e) => handleInputChange(form.id, 'status', e.target.value)}
                                                className="w-full bg-transparent border-b border-gray-200 px-0 py-2 text-gray-900 focus:outline-none focus:border-[#00BFA5] transition-colors rounded-none appearance-none cursor-pointer"
                                                required
                                            >
                                                <option value="submitted">Submitted</option>
                                            </select>
                                            <div className="absolute inset-y-0 right-0 flex items-center pointer-events-none text-gray-400">
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                </svg>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}

                        <div className="pt-2 flex items-center">
                            <label className="flex items-center space-x-3 cursor-pointer group">
                                <div className="relative flex items-center shrink-0">
                                    <input
                                        type="checkbox"
                                        checked={isChecked}
                                        onChange={(e) => setIsChecked(e.target.checked)}
                                        className="peer h-3.5 w-3.5 cursor-pointer appearance-none rounded border border-gray-300 transition-all checked:border-[#00BFA5] checked:bg-[#00BFA5]"
                                        required
                                    />
                                    <svg className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-2.5 h-2.5 text-white opacity-0 transition-opacity peer-checked:opacity-100" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                <span className="text-xs font-medium text-gray-400 group-hover:text-gray-500 transition-colors">
                                    The above mission is submitted after preview from the pre-QCER
                                </span>
                            </label>
                        </div>

                        <div className="py-2 text-center">
                            <button
                                type="button"
                                onClick={addForm}
                                className="text-[#0084FF] text-sm font-medium hover:text-blue-700 transition-colors flex items-center justify-center mx-auto space-x-1"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                </svg>
                                <span>Add Row</span>
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            <div className="fixed bottom-[80px] left-0 right-0 border-t border-gray-100 p-4 z-50">
                <div className="max-w-md mx-auto flex justify-center gap-4">
                    <button
                        type="button"
                        onClick={handleSkip}
                        disabled={isSubmitting}
                        className="w-full max-w-[120px] bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold py-2.5 rounded-lg shadow-sm transition active:scale-[0.98] text-sm uppercase tracking-wide"
                    >
                        Skip
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                        className="w-full max-w-[240px] bg-[#00BFA5] hover:bg-[#00a68f] disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-semibold py-2.5 rounded-lg shadow-sm transition active:scale-[0.98] text-sm uppercase tracking-wide"
                    >
                        {isSubmitting ? 'Submitting...' : (forms.length > 1 ? 'Submit All' : 'Submit')}
                    </button>
                </div>
            </div>

            <BottomNav />
        </div>
    )
};


export default Qcform;